/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Checkbox, Col, Empty, Row, Select, Space, Spin } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';

// Constant declarations
const baseClassName = 'multi-dropdown';

/**
  * Checks if all elements in target array are in source array
  * @param {Array} arr - the source array
  * @param {Array} target - the target array
*/
const includesAll = (arr, target) => target.every(v => arr.includes(v));

const MultiDropdown = ({
  value: values = [], // the list of values that have been selected - renamed within the component for clarity
  loading,
  onChange,
  disabled,
  className,
  options, // {value, label, searchValue, disabled, className, extra}[],
  hideExtraFromDropdown,
  ...rest
}) => {
  const { t } = useTranslation();

  const [checkedItemsInDropdown, setCheckedItemsInDropdown] = useState([]); // the list of items that have been checked in the dropdown (not necessarily added to the value)
  const [filteredOptionsInDropdown, setFilteredOptionsInDropdown] = useState([...options]); // the filtered (or unfiltered) list of options to show in the dropdown
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <Fragment>
      <Select
        open={dropdownOpen}
        onDropdownVisibleChange={(open) => {
          if (!open) {
            setCheckedItemsInDropdown([]);
            setFilteredOptionsInDropdown([...options]);
          }
          setDropdownOpen(open);
        }}
        disabled={disabled}
        mode="multiple"
        className={'fs-base' + (className ? ` ${className}` : '')}
        value={[]} // we don't want to display all the values within the input, as we have a custom display below
        popupClassName={`${baseClassName}__popup`}
        notFoundContent={
          <Row className="p-4" justify="center" align="middle">
            {
              loading ? (
                <Spin />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Nothing found')} />
              )
            }
          </Row>
        }
        showSearch
        showArrow
        dropdownRender={menu => (
          <Fragment>
            <Row
              className="px-3 pb-2 border-bottom"
              wrap={false}
              align="middle"
              justify="space-between"
            >
              <span
                className="cursor-pointer"
                onClick={() => {
                  if (includesAll([...new Set([...values, ...checkedItemsInDropdown])], filteredOptionsInDropdown.map(opt => opt.value))) {
                    setCheckedItemsInDropdown(checkedItemsInDropdown.filter(item => !filteredOptionsInDropdown.some(opt => opt.value === item)));
                  } else {
                    setCheckedItemsInDropdown([...new Set([...checkedItemsInDropdown, ...filteredOptionsInDropdown.map(opt => opt.value)])]);
                  }
                }}
              >
                <Checkbox
                  className="pointer-events-none"
                  checked={filteredOptionsInDropdown.length && includesAll([...new Set([...values, ...checkedItemsInDropdown])], filteredOptionsInDropdown.map(opt => opt.value))}
                >
                  {t('Select All')}
                </Checkbox>
              </span>

              <Button
                type="primary"
                className="mt-1"
                disabled={!checkedItemsInDropdown.length}
                onClick={() => {
                  setDropdownOpen(false); // Close the dropdown after adding items
                  onChange([...values, ...checkedItemsInDropdown]);
                  setCheckedItemsInDropdown([]);
                }}
              >
                {t('Add')}
              </Button>
            </Row>

            {menu}
          </Fragment>
        )}
        onSelect={(selectedItem) => {
          const existsInValues = checkedItemsInDropdown.find(value => value === selectedItem);

          if (existsInValues) {
            setCheckedItemsInDropdown(checkedItemsInDropdown.filter(item => item !== selectedItem));
          } else {
            setCheckedItemsInDropdown([...checkedItemsInDropdown, selectedItem]);
          }
        }}
        filterOption={() => filteredOptionsInDropdown.length}
        onSearch={(input) => {
          if (options) {
            setFilteredOptionsInDropdown([...options].filter(option => input ? (option.searchValue || '').toLowerCase().includes(input.toLowerCase()) : true));
          }
        }}
        getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
        {...rest} // Pass any other props down to the Select component
      >
        {
          filteredOptionsInDropdown.map(option => (
            <Select.Option key={option.value} value={option.value} disabled={option.disabled || values.includes(option.value)}>
              <Checkbox
                checked={checkedItemsInDropdown.includes(option.value) || values.includes(option.value)}
                disabled={option.disabled || values.includes(option.value)}
              >
                <span onClick={(e) => e.stopPropagation()}>
                  {option.label} {!hideExtraFromDropdown && option.extra}

                  <span className="ml-2 fw-500 text-primary fs-sm">{values.includes(option.value) ? t('added') : null}</span>
                </span>
              </Checkbox>
            </Select.Option>
          ))
        }
      </Select>

      {
        values?.length > 0 ? (
          <div className="bg-body position-relative mt-3 border-radius-base">
            <Row
              align="middle"
              justify="space-between border-bottom position-sticky top-0"
              className="px-4 py-2 fs-sm fw-500"
            >
              {values.length + ` items`}

              <Button
                type="link"
                size="small"
                className="fs-sm"
                onClick={() => onChange([])}
              >
                {t('Remove All')}
              </Button>
            </Row>

            <Space
              direction="vertical"
              className="overflow-y-auto w-100 px-4 py-2"
              size={12}
              style={{ maxHeight: 180 }}
            >
              {
                options.filter(opt => values.includes(opt.value)).map(item => (
                  <Row
                    wrap={false}
                    gutter={12}
                    align="middle"
                  >
                    <Col flex="auto">
                      <span className="d-flex align-items-center">{item.label} {item.extra}</span>
                    </Col>

                    <Col>
                      <Button
                        icon={<CloseCircleFilled />}
                        type="link"
                        shape="circle"
                        className="text-muted"
                        size="small"
                        onClick={() => onChange(values.filter(val => val !== item.value))}
                      />
                    </Col>
                  </Row>
                ))
              }
            </Space>
          </div>
        ) : null
      }
    </Fragment>
  );
};

MultiDropdown.propTypes = {
  value: PropTypes.array,
  loading: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  options: PropTypes.shape([{
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    value: PropTypes.string,
    searchValue: PropTypes.string, // the value to search on
    disabled: PropTypes.bool,
    extra: PropTypes.node
  }]),
  hideExtraFromDropdown: PropTypes.bool
};

export default MultiDropdown;

